'use client';

import { cn } from '@sharpr/ui';
import { BookName, BookThumbnail } from '@/components/BookThumbnail';
import { RichPopover } from '@sharpr/ui/rich-popover';
import { isExcluded, sortQuotesForDisplay, type LinePreference } from '@/utils/slateOddsBest';
import type { BookQuote } from '@/app/api/slate/route';
import { formatOdds } from '@sharpr/ui/format';

interface MarketOddsPopoverProps {
  /** Trigger element (the pill that's already on screen). */
  trigger: React.ReactNode;
  /** Title shown at the top of the popover/drawer. */
  title: string;
  /** All per-book quotes for this side. */
  quotes: ReadonlyArray<BookQuote>;
  /** Ids of books excluded from best-line calc — best row gets a star. */
  excludedFromBestLine: ReadonlyArray<string>;
  /** How to rank quotes (higher-point / lower-point / point-irrelevant). */
  preference: LinePreference;
  /** Optional formatter for the point value (e.g. spread "+1.5", total "8.5"). */
  formatPoint?: (point: number) => string;
  /** Market consensus point; enables banded filtering when `altBand` is also set. */
  consensusPoint?: number;
  /** Window around consensus to include in the popover (e.g. 0.5 shows consensus ±0.5). */
  altBand?: number;
}

export function MarketOddsPopover({
  trigger,
  title,
  quotes,
  excludedFromBestLine,
  preference,
  formatPoint,
  consensusPoint,
  altBand,
}: MarketOddsPopoverProps) {
  if (quotes.length === 0) {
    return <>{trigger}</>;
  }

  // When caller provides both a consensus point and a band, restrict rows to
  // that window (e.g. consensus=9.5, altBand=0.5 → points in [9.0, 10.0]).
  // Epsilon (1e-9) absorbs floating-point noise on .5 arithmetic.
  const banded = consensusPoint != null && altBand != null
    ? quotes.filter(q => q.point != null && Math.abs(q.point - consensusPoint) <= altBand + 1e-9)
    : quotes;
  const sorted = sortQuotesForDisplay(banded, preference).slice(0, 5);
  // Identify the best eligible book (after exclusions) so we can mark it in the list.
  const bestEligible = sorted.find(q => !isExcluded(q.book, excludedFromBestLine));
  const bestKey = bestEligible?.book;

  return (
    <RichPopover
      trigger={trigger}
      ariaLabel={title}
      header={<span className="text-sm font-semibold text-gray-100">{title}</span>}
      width={260}
      side="bottom"
      align="center"
      openOn="click"
      className="!bg-bg-secondary !border-border-secondary"
    >
      <div className="px-2 py-1.5">
        <div className="hidden md:block px-2 pt-1 pb-1.5 text-[11px] font-semibold text-gray-200">
          {title}
        </div>
        <ul>
          {sorted.map((q) => {
            const excluded = isExcluded(q.book, excludedFromBestLine);
            const isBest = q.book === bestKey;
            return (
              <li
                key={q.book}
                className={cn(
                  'flex items-center gap-2 px-2 py-1 text-[12px] font-mono',
                  excluded && 'opacity-40',
                )}
              >
                <BookThumbnail book={q.book} size="sm" className="rounded-sm object-contain" />
                <span className="flex-1 min-w-0 truncate text-gray-200 font-sans text-[11px]"><BookName book={q.book} /></span>
                {q.point != null && (
                  <span className="text-gray-300 tabular-nums">
                    {formatPoint ? formatPoint(q.point) : q.point}
                  </span>
                )}
                <span className={cn('tabular-nums w-12 text-right', isBest ? 'text-emerald-400 font-semibold' : 'text-gray-100')}>
                  {formatOdds(q.price)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </RichPopover>
  );
}
